import React, { FC } from "react";
import {
  TextField,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  LinearProgress,
  Box,
  Paper,
  SxProps,
  Theme,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { parseJwt } from "helpers/Jwt";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import { Checkmark } from "components/Icons";
import { useForm, FormState, schemas } from "helpers/FormHelpers";
import { useNavigate } from "react-router-dom";
import { backend } from "Backend";
import theme from "theme";

const schema = {
  password: schemas.password,
  confirmPassword: {
    equality: "password",
  },
};
const styles: { [k: string]: SxProps<Theme> } = {
  root: {
    backgroundColor: theme.palette.background.default,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    width: "100%",
  },
  container: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    backgroundColor: "white",
    padding: theme.spacing(2, 3),
  },
  form: {
    width: "400px",
  },
  textField: {
    minHeight: "80px",
  },
  title: {
    marginBottom: theme.spacing(2),
    width: "400px",
    color: theme.palette.primary.main,
  },

  barContainer: {
    minHeight: "30px",
  },
};

type Field = "password" | "confirmPassword";
const initial: FormState<Field> = {
  isValid: false,
  values: { password: "", confirmPassword: "" },
  touched: { password: false, confirmPassword: false },
  errors: {},
};

interface ResetPasswordToken {
  email: string;
}

const ResetPassword: FC = () => {
  const params = new URLSearchParams(document.location.search);
  const rawToken = params.get("token") || "";
  const isPerson = params.get("person") === "true";
  const destination: "collect" | "pattern" = params.get("dest") === "collect" ? "collect" : "pattern";
  let token: ResetPasswordToken;
  try {
    token = parseJwt<ResetPasswordToken>(rawToken);
  } catch (e) {
    console.error(e);
    token = { email: "" };
  }

  const navigate = useNavigate();

  const [showPassword, setShowPassword] = React.useState(false);

  const [submitError, setSubmitError] = React.useState("");

  const [loading, setLoading] = React.useState(false);

  const {
    formStateHandle: [formState],
    isGood,
    hasError,
    errorText,
    handleChange,
  } = useForm<Field>(initial, schema);

  const handleSubmit: React.FormEventHandler = async (event) => {
    event.preventDefault();
    setSubmitError("");
    if (rawToken) {
      setLoading(true);
      if (isPerson) {
        const response = await backend.changePersonPassword(rawToken, formState.values.password);
        setLoading(false);
        switch (response.tag) {
          case "success": {
            if (destination === "collect") {
              window.location.assign(response.value.collectUrl);
            } else {
              navigate("/signin");
            }
            break;
          }
          case "unauthorized": {
            setSubmitError("Invalid or expired token");
            break;
          }
          default: {
            setSubmitError("There was an error contacting the server");
            break;
          }
        }
      } else {
        const response = await backend.changePassword(rawToken, formState.values.password);
        setLoading(false);
        switch (response.tag) {
          case "success": {
            navigate("/signin");
            break;
          }
          case "error": {
            setSubmitError("There was an error contacting the server");
            break;
          }
          case "unauthorized": {
            setSubmitError("Invalid or expired token");
            break;
          }
          default:
            setSubmitError("There was an error contacting the server");
            break;
        }
      }
    }
  };

  if (!rawToken) return <div>Bad Token</div>;
  else
    return (
      <Box sx={styles.root}>
        <Typography sx={styles.title} align="center" variant="h5">
          Choose a new password
        </Typography>
        <form
          style={{
            width: "400px",
          }}
          noValidate
          autoComplete="off"
          onSubmit={handleSubmit}>
          <Paper sx={styles.container}>
            <TextField
              id="email"
              name="email"
              label="Email"
              size="medium"
              margin="normal"
              helperText="Already set"
              sx={styles.textField}
              disabled={true}
              value={token.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              id="password"
              name="password"
              label="Password"
              margin="normal"
              size="medium"
              placeholder="Choose a password"
              autoFocus
              helperText={errorText("password")}
              sx={styles.textField}
              type={showPassword ? "text" : "password"}
              value={formState.values.password}
              onChange={handleChange}
              error={hasError("password")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      tabIndex={-1}
                      onClick={() => setShowPassword(!showPassword)}
                      size="large">
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              id="confirmPassword"
              name="confirmPassword"
              label="Confirm Password"
              margin="normal"
              size="medium"
              sx={styles.textField}
              type={showPassword ? "text" : "password"}
              value={formState.values.confirmPassword}
              onChange={handleChange}
              error={hasError("confirmPassword")}
              helperText={errorText("confirmPassword")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">{isGood("confirmPassword") && <Checkmark />}</InputAdornment>
                ),
              }}
            />

            <Button
              sx={{
                height: "3rem",
              }}
              variant="contained"
              color="primary"
              type="submit"
              disabled={!formState.isValid}>
              Sign In
            </Button>

            <div
              style={{
                minHeight: "30px",
              }}>
              {submitError && (
                <Typography color="error" align="center" variant="caption">
                  {submitError}
                </Typography>
              )}
              <Box visibility={loading ? "visible" : "hidden"}>
                <LinearProgress />
              </Box>
            </div>
          </Paper>
        </form>
      </Box>
    );
};

export default ResetPassword;
